import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { useT } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, FileCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import RowActions from '../components/RowActions';
import EditEntityDialog from '../components/EditEntityDialog';
import { CompanyContext } from '../contexts/CompanyContext';
import { downloadEntityPdf } from '../lib/entityPdf';

const MANAGE = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER'];

export default function Certificates() {
  const t = useT();
  const { user } = useContext(AuthContext);
  const { company } = useContext(CompanyContext);
  const [items, setItems] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newCert, setNewCert] = useState({
    certificate_type: 'fire_safety',
    certificate_number: '',
    building_id: '',
    authority: 'Civil Defence',
    issue_date: new Date().toISOString().split('T')[0],
    expiry_date: new Date(Date.now() + 365 * 86400000).toISOString().split('T')[0]
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [c, b] = await Promise.all([
        axios.get(`${API}/certificates`, { withCredentials: true }),
        axios.get(`${API}/buildings`, { withCredentials: true })
      ]);
      setItems(c.data); setBuildings(b.data);
    } catch { toast.error('Failed'); }
    setLoading(false);
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/certificates`, {
        ...newCert,
        issue_date: new Date(newCert.issue_date).toISOString(),
        expiry_date: new Date(newCert.expiry_date).toISOString()
      }, { withCredentials: true });
      toast.success('Certificate added');
      setShowNew(false);
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await axios.delete(`${API}/certificates/${id}`, { withCredentials: true }); fetchData(); }
    catch { toast.error('Delete failed'); }
  };

  const daysUntil = (d) => Math.ceil((new Date(d) - new Date()) / 86400000);

  const downloadPdf = (c) => {
    const b = buildings.find(x => x.id === c.building_id);
    downloadEntityPdf({
      title: `Certificate · ${c.certificate_type.replace('_', ' ')}`,
      subtitle: c.certificate_number,
      company,
      fields: [
        { label: 'Type', value: c.certificate_type.replace('_', ' ') },
        { label: 'Number', value: c.certificate_number },
        { label: 'Authority', value: c.authority },
        { label: 'Building', value: b?.name || c.building_id },
        { label: 'Issue date', value: new Date(c.issue_date).toLocaleDateString() },
        { label: 'Expiry date', value: new Date(c.expiry_date).toLocaleDateString() },
        { label: 'Days remaining', value: daysUntil(c.expiry_date) },
        { label: 'Status', value: c.status || 'valid' },
      ],
    });
  };

  return (
    <Layout>
      <div data-testid="certificates-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">{t('cert.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('cert.subtitle')}</p>
            <p className="text-sm text-muted-foreground">Civil Defence, DEWA, ADDC and other compliance certificates</p>
          </div>
          {MANAGE.includes(user?.role) && (
            <Dialog open={showNew} onOpenChange={setShowNew}>
              <DialogTrigger asChild>
                <Button data-testid="new-cert-btn" className="bg-teal-600 hover:bg-teal-700"><Plus className="w-4 h-4 mr-1" />New Certificate</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add Certificate</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                  <div><Label>Type</Label>
                    <Select value={newCert.certificate_type} onValueChange={(v) => setNewCert({ ...newCert, certificate_type: v })}>
                      <SelectTrigger data-testid="cert-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fire_safety">Fire Safety</SelectItem>
                        <SelectItem value="electrical">Electrical</SelectItem>
                        <SelectItem value="structural">Structural</SelectItem>
                        <SelectItem value="occupancy">Occupancy</SelectItem>
                        <SelectItem value="lift">Lift/Elevator</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Building</Label>
                    <Select value={newCert.building_id} onValueChange={(v) => setNewCert({ ...newCert, building_id: v })}>
                      <SelectTrigger data-testid="cert-building-select"><SelectValue placeholder="Building" /></SelectTrigger>
                      <SelectContent>{buildings.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Cert Number</Label><Input data-testid="cert-number-input" value={newCert.certificate_number} onChange={(e) => setNewCert({ ...newCert, certificate_number: e.target.value })} /></div>
                    <div><Label>Authority</Label><Input value={newCert.authority} onChange={(e) => setNewCert({ ...newCert, authority: e.target.value })} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Issue Date</Label><Input type="date" value={newCert.issue_date} onChange={(e) => setNewCert({ ...newCert, issue_date: e.target.value })} /></div>
                    <div><Label>Expiry Date</Label><Input type="date" value={newCert.expiry_date} onChange={(e) => setNewCert({ ...newCert, expiry_date: e.target.value })} /></div>
                  </div>
                  <Button data-testid="submit-cert-btn" onClick={handleCreate} disabled={!newCert.certificate_number || !newCert.building_id} className="w-full bg-teal-600 hover:bg-teal-700">Add Certificate</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {loading ? <div className="py-12 text-center text-muted-foreground">Loading...</div> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((c) => {
              const days = daysUntil(c.expiry_date);
              const expiring = days <= 30;
              const expired = days < 0;
              return (
                <Card key={c.id} data-testid={`cert-card-${c.id}`} className={`p-5 ${expired ? 'border-l-4 border-l-red-500' : expiring ? 'border-l-4 border-l-amber-500' : ''}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="p-2 bg-teal-100 rounded-lg"><FileCheck className="w-5 h-5 text-teal-700" /></div>
                    <RowActions
                      testIdPrefix={`cert-${c.id}`}
                      onEdit={MANAGE.includes(user?.role) ? () => setEditingId(c.id) : undefined}
                      onDownloadPdf={() => downloadPdf(c)}
                      onDelete={MANAGE.includes(user?.role) ? () => handleDelete(c.id) : undefined}
                      deleteLabel={`certificate "${c.certificate_number}"`}
                    />
                  </div>
                  <h3 className="font-semibold text-foreground capitalize">{c.certificate_type.replace('_', ' ')}</h3>
                  <p className="text-xs text-muted-foreground mb-2">{c.certificate_number}</p>
                  <p className="text-xs text-slate-600 mb-3">{c.authority}</p>
                  <div className="flex items-center gap-1.5 text-xs">
                    {expired ? (
                      <span className="inline-flex items-center gap-1 text-red-600 font-semibold"><AlertTriangle className="w-3 h-3" />Expired {Math.abs(days)}d ago</span>
                    ) : expiring ? (
                      <span className="inline-flex items-center gap-1 text-amber-600 font-semibold"><AlertTriangle className="w-3 h-3" />Expires in {days}d</span>
                    ) : (
                      <span className="text-green-600 font-semibold">Valid · {days}d left</span>
                    )}
                  </div>
                </Card>
              );
            })}
            {items.length === 0 && (
              <Card className="p-10 text-center col-span-full">
                <FileCheck className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-muted-foreground">No certificates</p>
              </Card>
            )}
          </div>
        )}

        {editingId && (
          <EditEntityDialog
            open
            hideTrigger
            onOpenChange={(v) => { if (!v) setEditingId(null); }}
            item={items.find(x => x.id === editingId)}
            endpoint="certificates"
            title="Edit Certificate"
            onSaved={() => { setEditingId(null); fetchData(); }}
            fields={[
              { key: 'certificate_type', label: 'Type', type: 'select', options: [
                { value: 'fire_safety', label: 'Fire Safety' },
                { value: 'electrical', label: 'Electrical' },
                { value: 'structural', label: 'Structural' },
                { value: 'occupancy', label: 'Occupancy' },
                { value: 'lift', label: 'Lift/Elevator' },
                { value: 'other', label: 'Other' },
              ]},
              { key: 'certificate_number', label: 'Certificate #' },
              { key: 'authority', label: 'Authority' },
              { key: 'building_id', label: 'Building', type: 'select', options: buildings.map(b => ({ value: b.id, label: b.name })) },
              { key: 'issue_date', label: 'Issue Date', type: 'date' },
              { key: 'expiry_date', label: 'Expiry Date', type: 'date' },
            ]}
          />
        )}
      </div>
    </Layout>
  );
}
